import { ReactNode, useState } from 'react';
import { useForm, FieldValues, Control, SubmitHandler, UseFormProps, FormState, UseFormWatch } from 'react-hook-form';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

/**
 * Props for BaseForm component.
 * @template T FieldValues type
 * @template TResult Result type
 */
interface BaseFormProps<T extends FieldValues, TResult = T> {
    open: boolean;
    onClose: () => void;
    /**
     * onSubmit can return void or a result object: { success: boolean, message?: string }
     */
    onSubmit: (data: TResult) => void | Promise<void | { success: boolean; message?: string }>;
    title: string;
    children: (control: Control<T>, formState: FormState<T>, watch: UseFormWatch<T>) => ReactNode;
    defaultValues: T;
    transformData?: (data: T) => TResult;
    formOptions?: Omit<UseFormProps<T>, 'defaultValues'>;
    submitText?: string;
    cancelText?: string;
    customActions?: (formState: FormState<T>, handleSubmit: () => void, handleCancel: () => void) => ReactNode;
    loading?: boolean;
    resetOnSubmit?: boolean;
    closeOnSubmit?: boolean;
    dialogProps?: Partial<DialogProps>;
}

/**
 * BaseForm
 * Generic Form Wrapper Component for dialog forms.
 * Reduces boilerplate for form dialogs by handling common form setup and dialog structure.
 * @template T FieldValues type
 * @template TResult Result type
 * @param {BaseFormProps<T, TResult>} props - Props for BaseForm.
 * @returns {JSX.Element} Form dialog component.
 */
export function BaseForm<T extends FieldValues, TResult = T>({
    open,
    onClose,
    onSubmit,
    title,
    children,
    defaultValues,
    transformData,
    formOptions = {},
    submitText = 'Submit',
    cancelText = 'Cancel',
    customActions,
    loading = false,
    resetOnSubmit = true,
    closeOnSubmit = true,
    dialogProps,
}: BaseFormProps<T, TResult>) {
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const { control, handleSubmit, reset, formState, watch } = useForm<T>({
        mode: 'onChange',
        ...formOptions,
        defaultValues: defaultValues as any,
    });

    const onFormSubmit: SubmitHandler<T> = async (data) => {
        try {
            const payload = transformData ? transformData(data) : (data as unknown as TResult);
            try { console.debug('[BaseForm] onFormSubmit payload:', payload); } catch (e) {}
            let result;
            try {
                result = await onSubmit(payload);
            } catch (err) {
                console.error('[BaseForm] onFormSubmit error:', err);
                throw err;
            }
            if (result && typeof result === 'object' && 'success' in result) {
                setSnackbar({
                    open: true,
                    message: result.message || (result.success ? 'Operation successful.' : 'Operation failed.'),
                    severity: result.success ? 'success' : 'error',
                });
            }
            if (resetOnSubmit) {
                reset();
            }
            if (closeOnSubmit && (!result || (typeof result === 'object' && result.success))) {
                onClose();
            }
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'An unexpected error occurred';
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
        }
    };

    const handleCancel = () => {
        reset();
        setSnackbar({ ...snackbar, open: false });
        onClose();
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSubmitClick = () => {
        handleSubmit(onFormSubmit as any)();
    };

    return (
        <Dialog {...dialogProps} open={open} onClose={handleCancel}>
            <DialogTitle>{title}</DialogTitle>
            <form onSubmit={handleSubmit(onFormSubmit as any)} autoComplete='off'>
                <DialogContent>{children(control as any, formState, watch)}</DialogContent>
                <DialogActions>
                    {customActions ? (
                        customActions(formState, handleSubmitClick, handleCancel)
                    ) : (
                        <>
                            <Button onClick={handleCancel} disabled={loading}>
                                {cancelText}
                            </Button>
                            <Button
                                type='submit'
                                variant='contained'
                                color='primary'
                                disabled={!formState.isValid || loading}
                            >
                                {loading ? 'Submitting...' : submitText}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </form>

            {/* Success/Error notification */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}
